import { CookieKey, SignUpSource } from '@/constants';
import { authenticate, updateTrackedShipments } from '@/fetchers';
import { useGlobalState } from '@/hooks';
import type { LoginMethod } from '@/interfaces';
import { userInfoModify } from '@/utils';
import type { OriginalUserInfo } from '@/utils/umsDataModify.utils';
import type { CodeResponse } from '@react-oauth/google';
import Cookies from 'js-cookie';

export type AuthPayload = {
  code: CodeResponse['code'];
  signUpSource: SignUpSource;
  newsletterOptIn: boolean;
  country?: string;
  fromPpEmail?: string;
  gaClientId?: string;
  gaSessionId?: string;
  eventTimestamp?: string;
  cta?: string;
  authenMethod?: LoginMethod;
};

export const useAuthentication = () => {
  const { setIsLoggedIn, setUserInfo, setFirestoreUuid, pmtCurrentLanguageCode } = useGlobalState();

  /**
   *
   * @param {string} accessToken access token
   * @param {OriginalUserInfo} userInfo user info
   * @returns void
   */
  const PMLogin = (accessToken: string, userInfo: OriginalUserInfo) => {
    Cookies.set(CookieKey.UmsAccessToken, accessToken, {
      expires: 365,
    });
    setIsLoggedIn(true);
    const modifiedUserInfo = userInfoModify(userInfo);
    setUserInfo(modifiedUserInfo);

    if (userInfo) {
      Cookies.set(CookieKey.UserId, userInfo.id, { expires: 365 });
      setFirestoreUuid(userInfo.id);

      updateTrackedShipments({
        language: pmtCurrentLanguageCode,
        accessToken,
      });
    }
  };

  /**
   *
   * @param {string} code access token
   * @param {SignUpSource} signUpSource signup source PMT / PMC
   * @param {boolean} newsletterOptIn opt in to newsletter
   * @param {string} country country code
   * @returns {Promise<any>} data
   */
  const authPM = async ({
    code,
    signUpSource = SignUpSource.PMT,
    newsletterOptIn = false,
    country,
    gaClientId,
    gaSessionId,
    eventTimestamp,
    cta,
    authenMethod,
  }: AuthPayload) => {
    try {
      const response = await authenticate({
        mediumOrigin: 'google',
        code,
        country,
        signUpSource,
        newsletterOptIn,
        gaClientId,
        gaSessionId,
        eventTimestamp,
        cta,
        authenMethod,
      });

      const hasError = 'error' in response;

      if (!hasError) {
        return response;
      }
    } catch (error) {
      console.error(error);
    }
  };

  return { authPM, PMLogin };
};
